/**
 * The two actions that drive a run's review, read per reply. Investigation traffic
 * is not subscribed to here: the socket store writes it into the review store, so a
 * verdict lands whether or not the simulator is on screen.
 */

import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { aiFunctionService } from '@/services/ai-function.service';
import { EMPTY_REVIEW, runReviewsAtom, setInvestigationStateAtom, setJudgeStateAtom } from './review.store';

interface JudgeInput {
  ticketId: string | null;
  aiResponse: string;
  customerQuestion?: string;
  /** A reply typed here, judged against instead of the one the ticket carries. */
  humanResponse?: string;
}

interface InvestigateInput {
  langfuseTraceUrl: string;
  bucket: string | null;
  ticketId: string | null;
}

export const useRunReview = (requestId: string) => {
  const reviews = useAtomValue(runReviewsAtom);
  const setJudge = useSetAtom(setJudgeStateAtom);
  const setInvestigation = useSetAtom(setInvestigationStateAtom);
  const review = reviews[requestId] ?? EMPTY_REVIEW;

  const runJudge = useCallback(
    async (input: JudgeInput) => {
      setJudge(requestId, { status: 'judging' });
      try {
        const result = await aiFunctionService.judgePlaygroundRun(input);
        setJudge(requestId, { status: 'done', result, authored: Boolean(input.humanResponse?.trim()) });
      } catch (err) {
        const message = getApiErrorMessage(err, 'The comparison could not be run');
        setJudge(requestId, { status: 'failed', message });
        toast.error(message);
      }
    },
    [requestId, setJudge]
  );

  const runInvestigation = useCallback(
    async (input: InvestigateInput) => {
      // Set before the POST resolves, so a step that arrives while it is open has somewhere to land.
      setInvestigation(requestId, { status: 'running', steps: [] });
      try {
        await aiFunctionService.investigatePlaygroundRun({ requestId, ...input });
      } catch (err) {
        const message = getApiErrorMessage(err, 'The investigation could not be started');
        setInvestigation(requestId, { status: 'failed', steps: [], reason: message });
        toast.error(message);
      }
    },
    [requestId, setInvestigation]
  );

  return { ...review, runJudge, runInvestigation };
};
